import { useCallback, useEffect, useRef, useState } from "react"
import type { AxiosError } from "axios"
import {
  abandonSitSession,
  exitSitSession,
  resumeSitSession,
  saveSitProgress,
  submitSitSession,
  type SitAnswerPatch,
} from "../../../../api/studentPracticePaperSit"
import type { PracticePaperSession } from "../../../../api/studentPracticePapers"
import {
  buildAnswerPatches,
  cloneQuestions,
  mergeAutosaveCredentials,
  mergeSitServerSession,
  requireSitCredentials,
} from "../utils/sitSessionHelpers"
import { answerStatusFromHtml } from "../utils/hasAnswerContent"
import type { PaperQuestionState, PaperSession } from "../types"

/** Debounce typing / navigation before hitting autosave. */
const AUTOSAVE_MS = 5000

const unwrap = (res: {
  data?: { success?: boolean; data?: PracticePaperSession; message?: string }
}) => {
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || "Sit session request failed")
  }
  return res.data.data as PaperSession
}

const isRevisionConflict = (err: unknown): boolean => {
  const ax = err as AxiosError<{ message?: string; detail?: string }>
  if (ax?.response?.status !== 409) return false
  const msg = `${ax.response.data?.message || ax.response.data?.detail || ""}`
  return /revision|updated elsewhere/i.test(msg)
}

interface UseSitSessionArgs {
  initialSession: PaperSession
  onSync: (session: PaperSession) => void
}

/**
 * Live Sit Mode state: optimistic edits + 5s debounced autosave + exit/abandon/submit.
 * Autosave never replaces local answer drafts with the response (avoids wiping mid-type).
 */
const useSitSession = ({ initialSession, onSync }: UseSitSessionArgs) => {
  const [session, setSession] = useState<PaperSession>(initialSession)
  const [timeTotal, setTimeTotal] = useState(initialSession.totalTimeSpentSeconds)
  const [currentQ, setCurrentQ] = useState(initialSession.currentQuestionIndex || 0)
  const [isBusy, setIsBusy] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const sessionRef = useRef(session)
  const timeRef = useRef(timeTotal)
  const currentQRef = useRef(currentQ)
  const baselineRef = useRef<PaperQuestionState[]>(
    cloneQuestions(initialSession.questions)
  )
  const saveTimerRef = useRef<number | null>(null)
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())
  const unmountedRef = useRef(false)
  /** Ignore leave-flush during the first moments after mount (boot → sit race). */
  const leaveFlushReadyRef = useRef(false)

  useEffect(() => {
    sessionRef.current = session
  }, [session])

  useEffect(() => {
    timeRef.current = timeTotal
  }, [timeTotal])

  useEffect(() => {
    currentQRef.current = currentQ
  }, [currentQ])

  useEffect(() => {
    unmountedRef.current = false
    leaveFlushReadyRef.current = false
    const readyTimer = window.setTimeout(() => {
      leaveFlushReadyRef.current = true
    }, 750)
    return () => {
      unmountedRef.current = true
      window.clearTimeout(readyTimer)
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current)
    }
  }, [])

  useEffect(() => {
    const interval = window.setInterval(() => {
      setTimeTotal((t) => t + 1)
    }, 1000)
    return () => window.clearInterval(interval)
  }, [])

  const applySaveSuccess = useCallback(
    (remote: PaperSession, questionsAtSave: PaperQuestionState[]) => {
      if (unmountedRef.current) return
      const next = mergeAutosaveCredentials(sessionRef.current, remote)
      sessionRef.current = next
      setSession(next)
      baselineRef.current = questionsAtSave
      onSync(next)
      setSaveError(null)
    },
    [onSync]
  )

  const postSave = useCallback(
    async (snap: PaperSession, questionsAtSave: PaperQuestionState[]) => {
      const { sitToken, revision } = requireSitCredentials(snap)
      const answers: SitAnswerPatch[] = buildAnswerPatches(
        questionsAtSave,
        baselineRef.current
      )
      return saveSitProgress(snap.id, {
        sitToken,
        revision,
        currentQuestionIndex: currentQRef.current,
        totalTimeSpentSeconds: timeRef.current,
        answers,
      })
    },
    []
  )

  const refreshCredentials = useCallback(async () => {
    const snap = sessionRef.current
    const res = await resumeSitSession(snap.id)
    const remote = unwrap(res)
    const next = mergeAutosaveCredentials(sessionRef.current, remote)
    sessionRef.current = next
    setSession(next)
    onSync(next)
    return next
  }, [onSync])

  const flushSave = useCallback(async () => {
    const run = async () => {
      const snap = sessionRef.current
      if (!(snap.sitToken || "").trim()) return

      const questionsAtSave = cloneQuestions(snap.questions)
      try {
        const res = await postSave(snap, questionsAtSave)
        applySaveSuccess(unwrap(res), questionsAtSave)
      } catch (err) {
        if (!isRevisionConflict(err)) throw err
        // Start/resume/another tab bumped revision — refresh token+rev and retry once.
        const fresh = await refreshCredentials()
        if (unmountedRef.current) return
        const res = await postSave(fresh, questionsAtSave)
        applySaveSuccess(unwrap(res), questionsAtSave)
      }
    }

    saveChainRef.current = saveChainRef.current.catch(() => undefined).then(run)
    await saveChainRef.current
  }, [applySaveSuccess, postSave, refreshCredentials])

  const scheduleAutosave = useCallback(() => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current)
    saveTimerRef.current = window.setTimeout(() => {
      void flushSave().catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : "Could not autosave progress"
        setSaveError(message)
      })
    }, AUTOSAVE_MS)
  }, [flushSave])

  // Persist timer/answers when the tab hides or the page unloads so resume
  // does not lose the last few seconds (and resume no longer invents idle time).
  useEffect(() => {
    const persistOnLeave = () => {
      if (!leaveFlushReadyRef.current) return
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      void flushSave().catch(() => undefined)
    }
    const onVisibility = () => {
      if (document.visibilityState === "hidden") persistOnLeave()
    }
    window.addEventListener("pagehide", persistOnLeave)
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      window.removeEventListener("pagehide", persistOnLeave)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [flushSave])

  const commitQuestionStatus = useCallback(
    (snap: PaperSession, index: number): PaperSession => {
      const questions = snap.questions.map((q, i) => {
        if (i !== index) return q
        return {
          ...q,
          status: answerStatusFromHtml(q.answerText),
        }
      })
      const next = { ...snap, questions }
      sessionRef.current = next
      setSession(next)
      return next
    },
    []
  )

  const updateAnswer = useCallback(
    (index: number, answerText: string) => {
      setSession((prev) => {
        const questions = prev.questions.map((q, i) =>
          i === index ? { ...q, answerText } : q
        )
        const next = {
          ...prev,
          questions,
          currentQuestionIndex: index,
          totalTimeSpentSeconds: timeRef.current,
          lastActiveAt: new Date().toISOString(),
        }
        sessionRef.current = next
        return next
      })
      setCurrentQ(index)
      scheduleAutosave()
    },
    [scheduleAutosave]
  )

  const goToQuestion = useCallback(
    (index: number) => {
      const last = Math.max(0, sessionRef.current.questions.length - 1)
      const nextIndex = Math.max(0, Math.min(last, Math.trunc(index)))
      // Sequential papers only — no skipping ahead or jumping back more than one.
      if (Math.abs(nextIndex - currentQRef.current) !== 1) return
      setCurrentQ(nextIndex)
      setSession((prev) => {
        const next = {
          ...prev,
          currentQuestionIndex: nextIndex,
          totalTimeSpentSeconds: timeRef.current,
        }
        sessionRef.current = next
        return next
      })
      scheduleAutosave()
    },
    [scheduleAutosave]
  )

  const goNext = useCallback(() => {
    const snap = commitQuestionStatus(sessionRef.current, currentQRef.current)
    const last = Math.max(0, snap.questions.length - 1)
    const nextIndex = Math.min(last, currentQRef.current + 1)
    goToQuestion(nextIndex)
  }, [commitQuestionStatus, goToQuestion])

  const cancelPendingAutosave = () => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
  }

  const saveAndExit = useCallback(async () => {
    cancelPendingAutosave()
    setIsBusy(true)
    try {
      await saveChainRef.current.catch(() => undefined)
      const snap = sessionRef.current
      const { sitToken, revision } = requireSitCredentials(snap)
      const answers = buildAnswerPatches(snap.questions, baselineRef.current)
      const res = await exitSitSession(snap.id, {
        sitToken,
        revision,
        currentQuestionIndex: currentQRef.current,
        totalTimeSpentSeconds: timeRef.current,
        answers,
      })
      const remote = unwrap(res)
      const next = {
        ...mergeSitServerSession(snap, remote),
        // Prefer the latest local drafts we just sent over any response lag.
        questions: cloneQuestions(snap.questions),
        sitToken: null,
      }
      onSync(next)
      return next
    } finally {
      setIsBusy(false)
    }
  }, [onSync])

  const abandon = useCallback(async () => {
    cancelPendingAutosave()
    setIsBusy(true)
    try {
      await saveChainRef.current.catch(() => undefined)
      const snap = sessionRef.current
      const sitToken = (snap.sitToken || "").trim() || undefined
      const res = await abandonSitSession(snap.id, {
        sitToken,
        revision: snap.revision,
        currentQuestionIndex: currentQRef.current,
        totalTimeSpentSeconds: timeRef.current,
        answers: buildAnswerPatches(snap.questions, baselineRef.current),
      })
      const next = {
        ...mergeSitServerSession(snap, unwrap(res)),
        questions: cloneQuestions(snap.questions),
        sitToken: null,
        status: "abandoned" as const,
      }
      onSync(next)
      return next
    } finally {
      setIsBusy(false)
    }
  }, [onSync])

  const submit = useCallback(async () => {
    cancelPendingAutosave()
    setIsSubmitting(true)
    try {
      await saveChainRef.current.catch(() => undefined)
      const snap = commitQuestionStatus(
        sessionRef.current,
        currentQRef.current
      )
      const { sitToken, revision } = requireSitCredentials(snap)
      const idempotencyKey =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `submit_${snap.id}_${revision}_${Date.now()}`
      const res = await submitSitSession(snap.id, {
        sitToken,
        revision,
        currentQuestionIndex: currentQRef.current,
        totalTimeSpentSeconds: timeRef.current,
        answers: buildAnswerPatches(snap.questions, null),
        idempotencyKey,
      })
      // Submit response includes scores — use server questions.
      const next = {
        ...mergeSitServerSession(snap, unwrap(res)),
        sitToken: null,
      }
      onSync(next)
      return next
    } finally {
      setIsSubmitting(false)
    }
  }, [commitQuestionStatus, onSync])

  return {
    session,
    timeTotal,
    currentQ,
    isBusy,
    isSubmitting,
    saveError,
    updateAnswer,
    goToQuestion,
    goNext,
    saveAndExit,
    abandon,
    submit,
  }
}

export default useSitSession
